import type { MouseEvent } from 'react';

import { link, params } from '@aline/routes';
import { supportEmail } from 'src/config';
import type { OpenInAppTabOptions } from 'src/layouts/tabs/tab-context';

export const OPEN_IN_APP_TAB_EVENT = 'aline:open-in-app-tab';

declare global {
  interface WindowEventMap {
    [OPEN_IN_APP_TAB_EVENT]: CustomEvent<OpenInAppTabOptions>;
  }
}

export function getDecodedHash(): string {
  return decodeURIComponent(window.location.hash.replace('#', ''));
}

export function getDecodedSearch(): string {
  return decodeURIComponent(window.location.search);
}

export function updateUrlWithoutRefresh(url: string): void {
  window.history.replaceState(window.history.state ?? {}, '', url);
}

function getHasScheme(url: string): boolean {
  return /^[a-z][a-z\d+.-]*:/i.test(url);
}

/**
 * Absolute form of a link meant to leave the app. Stored and pasted URLs routinely omit the scheme
 * (`www.example.com/sla`), and both the URL parser and `window.open` resolve those against our own
 * origin — which silently turns an external link into a path we do not serve.
 */
export function getExternalUrl(url: string): string {
  const trimmed = url.trim();
  // Protocol-relative (`//host/path`) already resolves to the right host.
  if (!trimmed || trimmed.startsWith('//') || getHasScheme(trimmed)) return trimmed;

  return `https://${trimmed}`;
}

function getIsInternalRoute(url: string): boolean {
  // Protocol-relative URLs are external despite the leading slash.
  if (url.startsWith('//')) return false;
  // Relative paths are always internal
  if (url.startsWith('/')) return true;
  /**
   * A schemeless host such as `www.example.com/sla` parses as a path against our own origin, so
   * only a URL carrying its own scheme can be judged same-origin.
   */
  if (!getHasScheme(url)) return false;
  // Same-origin absolute URLs are internal
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin;
  } catch {
    return false;
  }
}

function extractPathname(url: string): string {
  if (url.startsWith('/')) return url;
  try {
    return (
      new URL(url, window.location.origin).pathname + new URL(url, window.location.origin).search
    );
  } catch {
    return url;
  }
}

export function openLink(url: string, options: Omit<OpenInAppTabOptions, 'route'> = {}) {
  if (getIsInternalRoute(url)) {
    const wasHandled = !window.dispatchEvent(
      new CustomEvent(OPEN_IN_APP_TAB_EVENT, {
        cancelable: true,
        detail: { route: extractPathname(url), ...options },
      })
    );
    if (wasHandled) return;
  }

  window.open(getExternalUrl(url), options.title || '_blank');
}

/**
 * Whether the browser should be left to handle a click itself. Modifier and non-primary clicks are
 * how users ask for a new tab or window, so in-app navigation must not swallow them.
 *
 * Structurally typed so it accepts both React's `MouseEvent` and the grid's `MuiEvent` wrapper.
 */
export function getIsBrowserHandledLinkClick(event: {
  altKey: boolean;
  button: number;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
}): boolean {
  return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
}

/**
 * Click handler for an in-app anchor whose target should open in an Aline tab. The anchor keeps a
 * real `href` so it can be copied, bookmarked and opened in a new browser tab, while a plain
 * left-click is taken over and routed through `openLink`.
 */
export function openLinkFromAnchorClick(
  event: MouseEvent<HTMLElement>,
  url: string,
  options: Omit<OpenInAppTabOptions, 'route'> = {}
): void {
  // Grid rows and cards below the link have their own click handlers.
  event.stopPropagation();
  if (getIsBrowserHandledLinkClick(event)) return;

  event.preventDefault();
  openLink(url, options);
}

export function openInSameWindow(url: string): void {
  window.open(url, '_self');
}

/**
 * Opens a document on one of its AI properties. The target is the summary route rather than the
 * document root because that is what opens the right drawer, and only the mounted drawer can
 * consume `questionId` and scroll to the question.
 *
 * `applyRouteToExistingTab` matters when a tab for the document is already open: tab handling
 * switches to it instead of opening a new one, and without the override the tab keeps its stored
 * route — landing on whatever section was open last, with the param dropped.
 */
export function openDocumentQuestion({
  documentId,
  questionId,
}: {
  documentId: string;
  questionId: string | null | undefined;
}): void {
  if (!questionId) {
    openLink(link.DocumentDetail({ documentId }), { applyRouteToExistingTab: true });
    return;
  }

  openLink(`${link.DocumentDetailSummary({ documentId })}?${params.questionId}=${questionId}`, {
    applyRouteToExistingTab: true,
  });
}

/**
 * Click handler for an anchor that should open an internal route in an Aline
 * tab instead of a browser tab. Plain left-clicks are intercepted and routed
 * through {@link openLink}; modifier, middle and right clicks fall through to
 * the anchor's native behavior so users can still open a browser tab or copy
 * the link.
 */
export function handleOpenInAlineTab(
  event: MouseEvent<HTMLAnchorElement>,
  route: string,
  options: Omit<OpenInAppTabOptions, 'route'> = {}
): void {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
    return;
  }
  event.preventDefault();
  openLink(route, options);
}

interface EmailSupportArgs {
  newTab?: boolean;
  subject?: string;
  to?: string;
}

export function emailWithSubject(options: EmailSupportArgs = {}): void {
  const {
    newTab = true,
    subject = `Aline Support: An error occurred`,
    to = supportEmail,
  } = options;
  const escapedSubject = escape(subject);
  const mailToURL = `mailto:${to}?subject=${escapedSubject}`;

  if (!newTab) return openInSameWindow(mailToURL);

  openLink(mailToURL);
}

/* location may be relative or fully qualified */
export function redirectTo(location: URL | string): void {
  const locationAssignment = typeof location === 'string' ? location : location.toString();
  window.location.assign(locationAssignment);
}
